import React, { FunctionComponent, ReactElement, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Dialog,
  Classes,
} from '@blueprintjs/core';
import { RootState } from 'data/root.reducers';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as authenticationActions from 'data/authentication/authentication.actions';

export interface LocationProps{
  location: {
    pathname: string,
    hash: string,
  }
}

const mapState = (state: RootState) => ({
  isAuthenticated: authenticationSelectors.isAuthenticated(state),
  isAuthenticationExpired: authenticationSelectors.isAuthenticationExpired(state),
  authenticationTimeout: authenticationSelectors.getAuthenticationTimeout(state),
});

const mapDispatch = {
  startAuthenticationTimeout: authenticationActions.startAuthenticationTimeout,
  handleLogout: authenticationActions.handleLogout,
  setCurrentPathname: authenticationActions.setCurrentPathname,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux & LocationProps;

export const SessionExpirationNotification: FunctionComponent<Props> = ({
  location,
  isAuthenticated,
  isAuthenticationExpired,
  authenticationTimeout,
  startAuthenticationTimeout,
  handleLogout,
  setCurrentPathname,
}: Props): ReactElement | null => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isAuthenticated && authenticationTimeout) {
      startAuthenticationTimeout(+authenticationTimeout);
    }

  }, [isAuthenticated, authenticationTimeout, startAuthenticationTimeout]);

  useEffect(() => {
    setCurrentPathname(location?.pathname);
  }, [location]);

  if (!isAuthenticated || !isAuthenticationExpired) {
    return null;
  }

  return (
    <Dialog
      title={t('sessionExpiration.title')}
      isOpen
      isCloseButtonShown={false}
    >
      <div className={Classes.DIALOG_BODY}>
        {t('sessionExpiration.desc')}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button intent="primary" onClick={handleLogout}>{t('common.button.ok')}</Button>
        </div>
      </div>
    </Dialog>
  );
};

export default withRouter(connector(SessionExpirationNotification));
